import { useIntl } from 'react-intl';

import { buildHoveredPointTooltipBodyProps } from './hoveredPointTooltipBodyUtils.js';
import { buildHoveredPointTooltipHeaderProps } from './hoveredPointTooltipHeaderUtils.js';
import { OverlayTooltip } from '../../../../core/components/overlay-tooltip/OverlayTooltip.js';
import type { SeriesItemProps } from '../../../../core/components/overlay-tooltip/types/series.js';
import { useAxesFormatter } from '../../../hooks/useAxesFormatter.js';
import { useBarData } from '../../../hooks/useBarData.js';
import { useFormatCategory } from '../../../hooks/useFormatCategory.js';
import { useXYChartColorScales } from '../../../hooks/useXYChartColorScales.js';
import { useXYChartData } from '../../../hooks/useXYChartData.js';
import { useXYChartTruncationMode } from '../../../hooks/useXYChartTruncationMode.js';
import type {
  HoveredDatapoint,
  HoveredSeries,
  HoveredSeriesToMetadata,
} from '../../../utils/type-guards.js';

export function HoveredPointTooltip<T extends HoveredSeries>({
  datapoint,
  series,
  seriesToMetadata,
}: Readonly<{
  datapoint: HoveredDatapoint<T>;
  series: T;
  seriesToMetadata: HoveredSeriesToMetadata<T>;
}>) {
  const { filteredSeriesWithDatapoints } = useBarData();
  const { axisIdToScaleDomain, datapointMetadataMap } = useXYChartData();
  const colorScaleMap = useXYChartColorScales();
  const { truncationMode } = useXYChartTruncationMode();
  const axisIdToFormatter = useAxesFormatter();
  const formatCategory = useFormatCategory();
  const intl = useIntl();

  const { name, value, color, actions } = buildHoveredPointTooltipHeaderProps({
    datapoint,
    series,
    seriesToMetadata,
    seriesWithDatapoints: filteredSeriesWithDatapoints,
    datapointMetadataMap,
    colorScaleMap,
  });

  const { yAxisValue, xAxisValue, xAxisDuration, yAxisDuration } =
    buildHoveredPointTooltipBodyProps({
      datapoint,
      series,
      seriesToMetadata,
      axisIdToFormatter,
      axisIdToScaleDomain,
      datapointMetadataMap,
      formatCategory,
      intl,
    });
  const isRect = series.type === 'rect';

  const yText = `${yAxisValue} ${yAxisDuration ? `(${yAxisDuration})` : ''}`;
  const data: SeriesItemProps[] = [
    {
      name: isRect ? yText : name,
      value: isRect ? (value as string) : yText,
      color,
    },
  ];
  const label = datapoint.x0 instanceof Date ? '' : undefined;

  return (
    <OverlayTooltip truncation={truncationMode}>
      <OverlayTooltip.Header
        header1={xAxisValue}
        duration1={xAxisDuration}
        label={label}
      />
      <OverlayTooltip.Series
        data={data}
        variant="shared"
        activeItem={-1}
        seriesItemTemplate={(item) => (
          <OverlayTooltip.SeriesItem {...item} actions={actions} />
        )}
      />
    </OverlayTooltip>
  );
}
